import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from './axiosInstance'
import { useMapStore, type WmsSourceConfig } from '../store/mapStore'

function getActiveSource(
  sources: WmsSourceConfig[],
  activeId: string | null
): WmsSourceConfig | null {
  if (sources.length === 0) return null
  if (activeId) {
    const found = sources.find((source) => source.id === activeId)
    if (found) return found
  }
  return sources[0]
}

function normalizeSettings(data: MapSettings): MapSettings {
  const sources = data.wmsSources ?? []
  const activeSource = getActiveSource(sources, data.activeWmsSourceId)

  return {
    ...data,
    wmsEndpoint: activeSource?.endpoint ?? data.wmsEndpoint,
    wmsLayers: activeSource?.layers ?? data.wmsLayers,
    activeWmsSourceId: activeSource?.id ?? null,
  }
}

export interface MapSettings {
  wmsEndpoint: string
  wmsLayers: string[]
  wmsSources: WmsSourceConfig[]
  activeWmsSourceId: string | null
  defaultCenterLon: number
  defaultCenterLat: number
  defaultZoom: number
  preferredCoordFormat: 'dd' | 'dms' | 'mgrs' | 'utm'
}

export function useMapSettings() {
  const setWmsEndpoint = useMapStore((s) => s.setWmsEndpoint)
  const setWmsLayers = useMapStore((s) => s.setWmsLayers)
  const setWmsSources = useMapStore((s) => s.setWmsSources)
  const setActiveWmsSourceId = useMapStore((s) => s.setActiveWmsSourceId)
  const setCenter = useMapStore((s) => s.setCenter)
  const setZoom = useMapStore((s) => s.setZoom)
  const setPreferredCoordFormat = useMapStore((s) => s.setPreferredCoordFormat)

  return useQuery({
    queryKey: ['settings', 'map'],
    queryFn: async () => {
      const res = await api.get<MapSettings>('/settings/map')
      const normalized = normalizeSettings(res.data)
      // Sync to map store
      setWmsEndpoint(normalized.wmsEndpoint)
      setWmsLayers(normalized.wmsLayers)
      setWmsSources(normalized.wmsSources)
      setActiveWmsSourceId(normalized.activeWmsSourceId)
      setCenter([normalized.defaultCenterLon, normalized.defaultCenterLat]) // [lon, lat] - GeoJSON order
      setZoom(normalized.defaultZoom)
      setPreferredCoordFormat(normalized.preferredCoordFormat)
      return normalized
    },
  })
}

export function useSaveMapSettings() {
  const qc = useQueryClient()
  const setWmsEndpoint = useMapStore((s) => s.setWmsEndpoint)
  const setWmsLayers = useMapStore((s) => s.setWmsLayers)
  const setWmsSources = useMapStore((s) => s.setWmsSources)
  const setActiveWmsSourceId = useMapStore((s) => s.setActiveWmsSourceId)
  const setCenter = useMapStore((s) => s.setCenter)
  const setZoom = useMapStore((s) => s.setZoom)
  const setPreferredCoordFormat = useMapStore((s) => s.setPreferredCoordFormat)

  return useMutation({
    mutationFn: async (settings: Partial<MapSettings>) => {
      const res = await api.put<MapSettings>('/settings/map', settings)
      return res.data
    },
    onSuccess: (data) => {
      const normalized = normalizeSettings(data)
      qc.setQueryData(['settings', 'map'], normalized)
      setWmsEndpoint(normalized.wmsEndpoint)
      setWmsLayers(normalized.wmsLayers)
      setWmsSources(normalized.wmsSources)
      setActiveWmsSourceId(normalized.activeWmsSourceId)
      setCenter([normalized.defaultCenterLon, normalized.defaultCenterLat]) // [lon, lat] - GeoJSON order
      setZoom(normalized.defaultZoom)
      setPreferredCoordFormat(normalized.preferredCoordFormat)
    },
  })
}
